import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import vehicleManagementDetailServices from '@src/services/vehicleManagement/vehicleManagementDetail.services';

export const getVehicleDetailList = createAsyncThunk(
  'getVehicleDetailList',
  async ({ customerId, id, providerType }, { getState, rejectWithValue }) => {
    try {
      const { data } = await vehicleManagementDetailServices.getVehicleDetailList({
        customerId,
        id,
        providerType,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getVehicleActivationCode = createAsyncThunk(
  'getVehicleActivationCode',
  async ({ customerId, vehicleId }, { getState, rejectWithValue }) => {
    try {
      const { data } = await vehicleManagementDetailServices.getVehicleActivationCode({
        customerId,
        vehicleId,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getUpdateVehicleLimits = createAsyncThunk(
  'getUpdateVehicleLimits',
  async ({ limitedValue }, { getState, rejectWithValue }) => {
    try {
      const response = await vehicleManagementDetailServices.getUpdateVehicleLimits({
        limitedValue,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getUpdateVehicleDisable = createAsyncThunk(
  'getUpdateVehicleDisable',
  async ({ request }, { getState, rejectWithValue }) => {
    try {
      const response = await vehicleManagementDetailServices.getUpdateVehicleDisable({
        request,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const postVehicleDeviceTypes = createAsyncThunk(
  'postVehicleDeviceTypes',
  async ({ vehicles }, { rejectWithValue }) => {
    try {
      const data = await vehicleManagementDetailServices.postVehicleDeviceTypes({ vehicles });
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const postUpdateVehicle = createAsyncThunk(
  'postUpdateVehicle',
  async ({ request }, { rejectWithValue }) => {
    try {
      const data = await vehicleManagementDetailServices.postUpdateVehicle({ request });
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const postUpdateVehicleDriver = createAsyncThunk(
  'postUpdateVehicleDriver',
  async ({ request }, { rejectWithValue }) => {
    try {
      const data = await vehicleManagementDetailServices.postUpdateVehicleDriver({ request });
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const postVehicleDeviceCancelRenew = createAsyncThunk(
  'postVehicleDeviceCancelRenew',
  async ({ request }, { rejectWithValue }) => {
    try {
      const data = await vehicleManagementDetailServices.postVehicleDeviceCancelRenew({ request });
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const deletePlateAndAllDevices = createAsyncThunk(
  'deletePlateAndAllDevices',
  async ({ request }, { getState, rejectWithValue }) => {
    try {
      const response = await vehicleManagementDetailServices.deletePlateAndAllDevices({
        request,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const permanentlyCancelVehicleDevice = createAsyncThunk(
  'permanentlyCancelVehicleDevice',
  async ({ request }, { getState, rejectWithValue }) => {
    try {
      const response = await vehicleManagementDetailServices.permanentlyCancelVehicleDevice({
        request,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const incrementActiveTab = (activeTab) => (dispatch, getState) => {
  dispatch(setActiveTab(activeTab));
};

export const decrementActiveTab = () => (dispatch, getState) => {
  const { activeTab } = getState().vehicleManagementDetail;
  dispatch(setActiveTab(activeTab - 1));
};

const initialState = {
  activeTab: '1',
  vehicleDetailList: [],
  vehicleManagementItem: {},
  loading: {
    vehicleDetailList: false,
    vehicleUpdateLimits: false,
    vehicleCancelDeviceModal: false,
    vehicleCancelDeviceRenewModal: false,
    postVehicleDeviceTypes: false,
    permanentlyCancelVehicleDeviceModal: false,
    postUpdateVehicle: false,
    postUpdateVehicleDriver: false,
    activeTab: false,
  },
};

export const vehicleManagementDetailSlice = createSlice({
  name: 'vehicleManagementDetail',
  initialState,
  reducers: {
    resetCurrentLoadVehicleManagementDetail: (state) => {
      return initialState;
    },
    setVehicleManagementItem: (state, action) => {
      state.vehicleManagementItem = action.payload;
    },
    setActiveTab: (state, action) => {
      state.loading.activeTab = true;
      state.activeTab = action.payload;
    },
    finishLoadingTab: (state) => {
      state.loading.activeTab = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVehicleDetailList.pending, (state) => {
        state.loading.vehicleDetailList = true;
      })
      .addCase(getUpdateVehicleLimits.pending, (state) => {
        state.loading.vehicleUpdateLimits = true;
      })
      .addCase(getUpdateVehicleDisable.pending, (state) => {
        state.loading.vehicleCancelDeviceModal = true;
      })
      .addCase(postVehicleDeviceCancelRenew.pending, (state) => {
        state.loading.vehicleCancelDeviceRenewModal = true;
      })
      .addCase(permanentlyCancelVehicleDevice.pending, (state) => {
        state.loading.permanentlyCancelVehicleDeviceModal = true;
      })
      .addCase(postVehicleDeviceTypes.pending, (state) => {
        state.loading.postVehicleDeviceTypes = true;
      })
      .addCase(postUpdateVehicle.pending, (state) => {
        state.loading.postUpdateVehicle = true;
      })
      .addCase(postUpdateVehicleDriver.pending, (state) => {
        state.loading.postUpdateVehicleDriver = true;
      })
      .addCase(getVehicleDetailList.fulfilled, (state, action) => {
        state.loading.vehicleDetailList = false;
        state.vehicleDetailList = action?.payload || [];
      })
      .addCase(getUpdateVehicleLimits.fulfilled, (state, action) => {
        state.loading.vehicleUpdateLimits = false;
      })
      .addCase(getUpdateVehicleDisable.fulfilled, (state, action) => {
        state.loading.vehicleCancelDeviceModal = false;
      })
      .addCase(postVehicleDeviceCancelRenew.fulfilled, (state, action) => {
        state.loading.vehicleCancelDeviceRenewModal = false;
      })
      .addCase(permanentlyCancelVehicleDevice.fulfilled, (state, action) => {
        state.loading.permanentlyCancelVehicleDeviceModal = false;
      })
      .addCase(postVehicleDeviceTypes.fulfilled, (state) => {
        state.loading.postVehicleDeviceTypes = false;
      })
      .addCase(postUpdateVehicle.fulfilled, (state) => {
        state.loading.postUpdateVehicle = false;
      })
      .addCase(postUpdateVehicleDriver.fulfilled, (state) => {
        state.loading.postUpdateVehicleDriver = false;
      })
      .addCase(getVehicleDetailList.rejected, (state, action) => {
        state.loading.vehicleDetailList = false;
        state.vehicleDetailList = [];
        state.error = action.payload || 'Error';
      })
      .addCase(getUpdateVehicleLimits.rejected, (state, action) => {
        state.loading.vehicleUpdateLimits = false;
        state.error = action.payload || 'Error';
      })
      .addCase(getUpdateVehicleDisable.rejected, (state, action) => {
        state.loading.vehicleCancelDeviceModal = false;
        state.error = action.payload || 'Error';
      })
      .addCase(postVehicleDeviceCancelRenew.rejected, (state, action) => {
        state.loading.vehicleCancelDeviceRenewModal = false;
        state.error = action.payload || 'Error';
      })
      .addCase(permanentlyCancelVehicleDevice.rejected, (state, action) => {
        state.loading.permanentlyCancelVehicleDeviceModal = false;
        state.error = action.payload || 'Error';
      })
      .addCase(postVehicleDeviceTypes.rejected, (state, action) => {
        state.loading.postVehicleDeviceTypes = false;
        state.error = action.payload || 'Error';
      })
      .addCase(postUpdateVehicle.rejected, (state, action) => {
        state.loading.postUpdateVehicle = false;
        state.error = action.payload || 'Error';
      })
      .addCase(postUpdateVehicleDriver.rejected, (state, action) => {
        state.loading.postUpdateVehicleDriver = false;
        state.error = action.payload || 'Error';
      });
  },
});

export const {
  resetCurrentLoadVehicleManagementDetail,
  setVehicleManagementItem,
  setActiveTab,
  finishLoadingTab,
} = vehicleManagementDetailSlice.actions;
